// frontend/src/components/admin/SpellStatsDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { format, subDays, differenceInDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import SpellSettings from './SpellSettings';
import './Admin.css';

// ============ Типы данных ============

interface SummaryStats {
  period_days: number;
  total_corrections: number;
  avg_daily_operators: number;
  total_tokens: number;
  cache_hit_rate: number;
  avg_response_time_ms: number;
}

interface OperatorStat {
  operator_id: number;
  operator_name: string;
  corrections: number;
  tokens: number;
  avg_time_ms: number;
}

interface FrequentError {
  original_text: string;
  corrected_text: string;
  frequency: number;
}

interface HourlyDistribution {
  hour: number;
  corrections: number;
}

interface RealtimeFeedItem {
  id: number;
  operator_name: string | null;
  original_text: string;
  corrected_text: string;
  created_at: string;
  diff_preview: string;
}

interface DailyStat {
  date: string;
  corrections: number;
  tokens: number;
  cache_hits: number;
}

// ============ Основной компонент ============

export const SpellStatsDashboard: React.FC = () => {
  const [period, setPeriod] = useState<7 | 30 | 90>(30);
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [topOperators, setTopOperators] = useState<OperatorStat[]>([]);
  const [frequentErrors, setFrequentErrors] = useState<FrequentError[]>([]);
  const [hourlyData, setHourlyData] = useState<HourlyDistribution[]>([]);
  const [realtimeFeed, setRealtimeFeed] = useState<RealtimeFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'operators' | 'errors' | 'realtime'>('overview');

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, dailyRes, operatorsRes, errorsRes, hourlyRes, feedRes] = await Promise.all([
        api.get(`/api/spell/stats/summary?days=${period}`),
        api.get(`/api/spell/stats/daily?days=${period}`),
        api.get(`/api/spell/stats/top-operators?days=${period}`),
        api.get(`/api/spell/stats/frequent-errors?days=${period}`),
        api.get(`/api/spell/stats/hourly-distribution?days=${period}`),
        api.get(`/api/spell/stats/realtime-feed?limit=20`)
      ]);

      setSummary(summaryRes.data);
      setDailyStats(dailyRes.data);
      setTopOperators(operatorsRes.data);
      setFrequentErrors(errorsRes.data);
      setHourlyData(hourlyRes.data);
      setRealtimeFeed(feedRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Auto-refresh для realtime ленты
  useEffect(() => {
    if (activeTab !== 'realtime') return;
    
    const interval = setInterval(() => {
      api.get('/api/spell/stats/realtime-feed?limit=20')
        .then(res => setRealtimeFeed(res.data))
        .catch(console.error);
    }, 10000); // каждые 10 секунд
    
    return () => clearInterval(interval);
  }, [activeTab]);

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <div className="admin-header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => (window.location.href = '/dashboard')} className="header-btn" title="Назад">
              <i className="bi bi-arrow-left"></i>
            </button>
            <h2 style={{ margin: 0 }}><i className="bi bi-spellcheck"></i> Орфография</h2>
          </div>
          <div className="admin-header-actions">
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value) as 7 | 30 | 90)}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', fontSize: 13 }}
            >
              <option value={7}>7 дней</option>
              <option value={30}>30 дней</option>
              <option value={90}>90 дней</option>
            </select>
            <button onClick={() => loadStats()} className="header-btn" title="Обновить">
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>

        {/* Yandex settings — главная плашка вверху, рендерим ВСЕГДА */}
        <SpellSettings />

        {loading ? (
          <div className="admin-card" style={{ marginTop: 16, padding: 32, textAlign: 'center', color: '#6b7280' }}>
            <i className="bi bi-hourglass-split" style={{ fontSize: 24, marginBottom: 8, display: 'block' }}></i>
            Загрузка статистики...
          </div>
        ) : (
        <div className="admin-card" style={{ marginTop: 16 }}>
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen" style={{ padding: 0, background: 'transparent', minHeight: 0 }}>
      {/* Заголовок */}
      <div className="flex justify-between items-center" style={{ display: 'none' }}>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 Статистика орфографии</h1>
          <p className="text-sm text-gray-500 mt-1">
            Анализ исправлений и эффективности работы YandexGPT
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value) as 7 | 30 | 90)}
            className="px-3 py-2 border rounded-lg bg-white text-sm"
          >
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
          </select>
          <button
            onClick={() => loadStats()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
          >
            Обновить
          </button>
        </div>
      </div>

      {/* Карточки с метриками */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Всего исправлений"
          value={summary?.total_corrections || 0}
          icon="📝"
          color="blue"
        />
        <MetricCard
          title="Экономия токенов (кэш)"
          value={`${summary?.cache_hit_rate || 0}%`}
          icon="💾"
          color="green"
          subtitle={`${Math.round((summary?.cache_hit_rate || 0) * (summary?.total_tokens || 0) / 100)} токенов сэкономлено`}
        />
        <MetricCard
          title="Потрачено токенов"
          value={summary?.total_tokens?.toLocaleString() || '0'}
          icon="🎯"
          color="orange"
        />
        <MetricCard
          title="Ср. время ответа"
          value={`${Math.round(summary?.avg_response_time_ms || 0)} мс`}
          icon="⚡"
          color="purple"
        />
      </div>

      {/* Вкладки */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { key: 'overview', label: 'Обзор', icon: '📈' },
            { key: 'operators', label: 'Операторы', icon: '👥' },
            { key: 'errors', label: 'Частые ошибки', icon: '📝' },
            { key: 'realtime', label: 'Live лента', icon: '🔄' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Контент вкладок */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* График динамики */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">📈 Динамика исправлений</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(new Date(date), 'dd MMM', { locale: ru })}
                  />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'dd MMMM yyyy', { locale: ru })}
                  />
                  <Legend />
                  <Area 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="corrections" 
                    name="Исправлений"
                    stroke="#3b82f6" 
                    fill="#93c5fd"
                    fillOpacity={0.3}
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="cache_hits" 
                    name="Из кэша"
                    stroke="#10b981" 
                    fill="#6ee7b7"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Распределение по часам */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">⏰ Активность по часам</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} />
                  <YAxis />
                  <Tooltip labelFormatter={(h) => `${h}:00 - ${h + 1}:00`} />
                  <Bar dataKey="corrections" name="Исправлений" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {activeTab === 'operators' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Оператор</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Исправлений</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Токенов</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ср. время</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Прогресс</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {topOperators.map((op, idx) => {
                  const maxCorrections = Math.max(...topOperators.map(o => o.corrections), 1);
                  const widthPercent = (op.corrections / maxCorrections) * 100;
                  
                  return (
                    <tr key={op.operator_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">
                            {idx === 0 ? '🏆' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '👤'}
                          </span>
                          <span className="font-medium text-gray-900">{op.operator_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                        {op.corrections}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {op.tokens.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {op.avg_time_ms} мс
                      </td>
                      <td className="px-6 py-4 w-full">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${widthPercent}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 mt-1">
                          {Math.round(widthPercent)}% от лидера
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {topOperators.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      Нет данных об операторах за выбранный период
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'errors' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">📝 Самые частые исправления</h3>
            <div className="space-y-3">
              {frequentErrors.map((error, idx) => {
                const maxFreq = Math.max(...frequentErrors.map(e => e.frequency), 1);
                const widthPercent = (error.frequency / maxFreq) * 100;
                
                return (
                  <div key={idx} className="group">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="font-mono text-gray-700">
                        <span className="text-red-500">"{error.original_text}"</span>
                        <span className="mx-2 text-gray-400">→</span>
                        <span className="text-green-600">"{error.corrected_text}"</span>
                      </div>
                      <span className="text-gray-500 text-xs">{error.frequency} раз(а)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {frequentErrors.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  Нет частых ошибок за выбранный период
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'realtime' && (
          <div className="space-y-3">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50 border-b">
                <span className="text-sm text-gray-500">
                  🔄 Обновляется каждые 10 секунд
                </span>
              </div>
              <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {realtimeFeed.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-gray-50 transition">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm">
                        {item.operator_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {item.operator_name || 'Система'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {format(new Date(item.created_at), 'HH:mm:ss')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 font-mono">
                          {item.diff_preview}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {realtimeFeed.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    Нет активных исправлений
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
        </div>
        )}
      </div>
    </div>
  );
};

// ============ Вспомогательный компонент MetricCard ============

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  icon: string;
  color: 'blue' | 'green' | 'orange' | 'purple';
  subtitle?: string;
}> = ({ title, value, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <div className={`w-8 h-8 rounded-full ${colorClasses[color]} flex items-center justify-center text-sm font-bold`}>
          {typeof value === 'number' && value > 0 ? '↑' : '•'}
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

export default SpellStatsDashboard;
